"use client";

import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { getDataAutomationGroup, getDataLinkAttributes } from "@/helpers";
import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import { ALPHABET, AlphabetKey, isAlphabetKey, Tabs } from "./Glossary";
import { GlossaryTableHeaders } from "./ITGlossary";
import { Button } from "../Button";

export interface GlossaryDesktopProps {
  selectedLetter: AlphabetKey;
  setSelectedLetter: (letter: AlphabetKey) => void;
  tabs: Tabs;
  tableHeaders: GlossaryTableHeaders;
}

const GlossaryDesktop = ({
  selectedLetter,
  setSelectedLetter,
  tabs,
  tableHeaders,
}: GlossaryDesktopProps) => {
  const navRef = useRef<HTMLDivElement | null>(null);
  const [isSticky, setIsSticky] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        const navPosition = navRef.current.getBoundingClientRect();
        const isSticky = navPosition.top <= 0;
        setIsSticky(isSticky);
      }
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <TabGroup
      selectedIndex={ALPHABET.indexOf(selectedLetter)}
      onChange={(index) => {
        const letter = ALPHABET[index];
        if (isAlphabetKey(letter)) {
          setSelectedLetter(letter);
        }
      }}
      as="div"
      className="hidden md:block md:py-16"
    >
      <TabList
        ref={navRef}
        className={cn(
          "sticky top-0 right-0 left-0 z-10 w-full py-4",
          isSticky && "bg-white shadow-[0_10px_20px_rgba(0,0,0,0.10)]",
        )}
      >
        <div className="swdc-container mx-auto flex w-full flex-row justify-between lg:gap-2 xl:gap-4">
          {ALPHABET.map((letter) => (
            <Tab
              key={letter}
              disabled={tabs[letter].length === 0}
              className={cn(
                "text-body-s text-swdc-black-600 flex h-6 w-[1.7875rem] shrink-0 items-center justify-center border border-transparent font-bold outline-none",
                tabs[letter].length > 0
                  ? "hover:bg-swdc-silver-200 active:text-swdc-gray-400 data-selected:border-swdc-orange-400"
                  : "text-swdc-gray-100 cursor-not-allowed",
              )}
              {...getDataLinkAttributes({
                dld: "Glossary letter Button",
                dlt: "IT Glossary",
              })}
            >
              {letter}
            </Tab>
          ))}
        </div>
      </TabList>
      <TabPanels className="swdc-container mx-auto">
        {ALPHABET.map((letter) => (
          <TabPanel unmount={false} key={letter} className="flex flex-col">
            <p className="text-subhead-xl text-swdc-black-600 pt-4 pb-4 text-left font-black">
              {letter}
            </p>
            <table
              className="w-full table-fixed border-collapse"
              {...getDataAutomationGroup("table")}
            >
              <thead>
                <tr className="border-swdc-silver-300 text-body-s text-swdc-gray-400 border bg-[#F4F7F7] p-4 text-left font-bold">
                  <th scope="col" className="w-74 p-4">
                    <div className="border-swdc-silver-300 mr-4 w-full border-r-1 pr-4">
                      {tableHeaders.firstColumn}
                    </div>
                  </th>
                  <th scope="col" className="w-auto p-4 pr-4.25 pl-0">
                    <div className="border-swdc-silver-300 mr-4.25 w-full border-r-1 pr-4">
                      {tableHeaders.secondColumn}
                    </div>
                  </th>
                  <th scope="col" className="w-74 p-4 pl-0">
                    <div className="w-full">{tableHeaders.thirdColumn}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tabs[letter].map((entry, index) => {
                  return (
                    <tr
                      key={index}
                      className="border-swdc-silver-300 text-body-s text-swdc-gray-300 border-b text-left"
                    >
                      <th scope="row" className="mr-4 py-2 pr-0 pl-4">
                        {entry.headline?.href && (
                          <div className="inline-flex flex-col items-center">
                            <Button
                              variant="tertiary"
                              size="s"
                              icon="none"
                              className="self-start pr-8 underline hover:decoration-2 active:decoration-2"
                              href={entry.headline.href}
                              dld={entry.headline?.dld}
                              dlt={entry.headline?.dlt}
                            >
                              {entry.headline?.title}
                            </Button>
                          </div>
                        )}
                      </th>
                      <td className="py-2">
                        <div className="border-swdc-silver-300 mr-4 -ml-4.5 border-x-1 px-4">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: entry.description || "",
                            }}
                          />
                        </div>
                      </td>
                      <td className="py-2 pr-4 pl-0">
                        {entry.solution?.href && (
                          <div className="inline-flex flex-col items-center">
                            <Button
                              variant="tertiary"
                              size="s"
                              icon="none"
                              className="self-start underline hover:decoration-2 active:decoration-2"
                              href={entry.solution.href}
                              dld={entry.solution?.dld}
                              dlt={entry.solution?.dlt}
                            >
                              {entry.solution?.title}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TabPanel>
        ))}
      </TabPanels>
    </TabGroup>
  );
};

export default GlossaryDesktop;
